import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { UploadFile, Close, Visibility, CloudUpload } from "@mui/icons-material";

export interface UploadedFile {
  id: string;
  fileName: string;
  presignedUrl: string;
  fileType?: string;
}

interface FileUploadSectionProps {
  title: string;
  selectedFiles: File[];
  uploadedFiles: UploadedFile[];
  onFileSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  onUpload?: () => Promise<void>;
  onPreview: (url: string) => void;
  isLoading?: boolean;
  accept?: string;
  buttonText?: string;
  uploadButtonText?: string;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  title,
  selectedFiles,
  uploadedFiles,
  onFileSelect,
  onFileRemove,
  onUpload,
  onPreview,
  isLoading = false,
  accept = "image/*,.pdf",
  buttonText = "Select Files",
  uploadButtonText = "Upload Selected Files"
}) => {
  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    onFileSelect(Array.from(files));
  };

  const renderSelectedFiles = (files: File[]) => (
    <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
      {files.map((file, idx) => (
        <Box
          key={idx}
          display="flex"
          alignItems="center"
          sx={{ 
            border: "1px solid #ccc", 
            borderRadius: 1, 
            p: 1,
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
          }}
        >
          {file.type.startsWith('image/') ? (
            <img
              src={URL.createObjectURL(file)}
              alt={`preview-${idx}`}
              width={80}
              height={60}
              style={{ objectFit: "cover", borderRadius: 4 }}
            />
          ) : (
            <Box
              width={80}
              height={60}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              <Typography variant="caption">{file.name.split('.').pop()?.toUpperCase()}</Typography>
            </Box>
          )}
          <Box ml={1} display="flex" alignItems="center">
            <Typography variant="caption" noWrap sx={{ maxWidth: 150, mr: 1 }}>
              {file.name}
            </Typography>
            <IconButton
              onClick={() => onFileRemove(idx)}
              size="small"
              sx={{ ml: 'auto' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderUploadedFiles = (files: UploadedFile[]) => (
    <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
      {files.map((file) => (
        <Box
          key={file.id}
          display="flex"
          alignItems="center"
          sx={{ 
            border: "1px solid #ccc", 
            borderRadius: 1, 
            p: 1,
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
          }}
        >
          <Typography noWrap sx={{ maxWidth: "150px", mr: 1 }}>
            {file.fileName}
          </Typography>
          <IconButton
            onClick={() => onPreview(file.presignedUrl)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFile />}
          fullWidth
        >
          {buttonText}
          <input
            type="file"
            hidden
            multiple
            accept={accept}
            onChange={handleFileSelection}
          />
        </Button>
      </Grid>

      {selectedFiles.length > 0 && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Files
            </Typography>
            {renderSelectedFiles(selectedFiles)}
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isLoading ? <CircularProgress size={20} /> : <CloudUpload />}
              onClick={onUpload}
              disabled={isLoading}
              fullWidth
            >
              {uploadButtonText}
            </Button>
          </Grid>
        </>
      )}

      {uploadedFiles.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Uploaded Files
          </Typography>
          {renderUploadedFiles(uploadedFiles)}
        </Grid>
      )}
    </Grid>
  );
};

export default FileUploadSection; 